import { useState, useRef, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, ReferenceLine, LabelList, Cell } from 'recharts';

// Add global styles to remove focus outlines
const chartStyles = `
  .recharts-wrapper,
  .recharts-surface,
  .recharts-bar-rectangle,
  .recharts-rectangle {
    outline: none !important;
  }
  .recharts-wrapper:focus,
  .recharts-surface:focus,
  .recharts-wrapper *:focus {
    outline: none !important;
  }
`;

const VerticalBarChart = ({ 
  data = [], 
  xAxisLabel = 'X Axis', 
  yAxisLabel = 'Y Axis', 
  colors = ['#003C73', '#76C8E8', '#B0EAC0','#FBCF4E','#F6A4A4','#C9B0D5'],
  barSize = 40,
  height = 400,
  showReferenceLine = false,
  referenceLineValue = 50,
  domain = [0, 100],
  ticks = [0, 20, 40, 60, 80, 100],
  valueFormatter = (value) => value,
  visibleBars = 16
}) => {
  const [visibleStartIndex, setVisibleStartIndex] = useState(0);
  const scrollContainerRef = useRef(null);
  const [viewportWidth, setViewportWidth] = useState(0);

  // For horizontal scrolling, show all data when scrollbar is needed
  const visibleData = data.length > visibleBars ? data : data.slice(visibleStartIndex, visibleStartIndex + visibleBars);
  const showScrollbar = data.length > visibleBars;

  // Calculate dynamic bar size based on number of bars
  const calculateDynamicBarSize = (numBars) => {
    if (numBars === 1) return 177;
    if (numBars === 2) return 177;
    if (numBars === 3) return 177;
    if (numBars === 4) return 139;
    if (numBars === 5) return 104;
    // For 6 or more bars, use the provided barSize prop
    return barSize;
  };

  const dynamicBarSize = showScrollbar ? barSize : calculateDynamicBarSize(visibleData.length);

  // Calculate dynamic domain from actual data
  const allValues = Array.isArray(data) ? data.map(d => Number(d?.value ?? 0)) : [];
  const maxVal = allValues.length ? Math.max(...allValues, 0) : 100;
  
  // Smart rounding for better upper bounds
  const calculateUpperBound = (max) => {
    if (max === 0) return 1;
    
    const withPadding = max * 1.2; // 20% padding for better visualization
    
    // Find the order of magnitude
    const magnitude = Math.pow(10, Math.floor(Math.log10(withPadding)));
    
    // Round up to the next "nice" number
    const normalized = withPadding / magnitude;
    let roundedNormalized;
    
    if (normalized <= 1) roundedNormalized = 1;
    else if (normalized <= 2) roundedNormalized = 2;
    else if (normalized <= 5) roundedNormalized = 5;
    else roundedNormalized = 10;
    
    return roundedNormalized * magnitude;
  };
  
  const roundedMax = calculateUpperBound(maxVal);
  const computedDomain = [0, roundedMax];

  // Calculate spacer width for horizontal scrolling
  const spacerWidth = viewportWidth > 0
    ? Math.max(viewportWidth, data.length * (viewportWidth / visibleBars))
    : '100%';

  useEffect(() => {
    const updateWidth = () => {
      if (scrollContainerRef.current) {
        setViewportWidth(scrollContainerRef.current.clientWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Natural horizontal scrolling - no pagination needed

  // Custom tick renderer for X-axis with improved multi-line support
  const renderXAxisTick = (props) => {
    const { x, y, payload } = props;
    const text = payload?.value || '';
    const words = text.split(' ');
    
    // More aggressive line splitting for tighter spaces (max 1 word per line for better readability)
    const lines = [];
    const wordsPerLine = 1;
    for (let i = 0; i < words.length; i += wordsPerLine) {
      lines.push(words.slice(i, i + wordsPerLine).join(' '));
    }
    
    // Reduce line height and font size for better fit
    const lineHeight = 10;
    const fontSize = 10;
    const startY = y + 5;
    
    return (
      <text x={x} textAnchor="middle" fill="var(--text-primary)" fontSize={fontSize}>
        {lines.map((line, index) => (
          <tspan key={index} x={x} y={startY + (index * lineHeight)}>
            {line}
          </tspan>
        ))}
      </text>
    );
  };

  if (!data || data.length === 0) {
    return (
      <div className="w-full h-[400px] flex items-center justify-center text-textPrimary">
        No data available
      </div>
    );
  }



  return (
    <>
      <style>{chartStyles}</style>
      <div className="w-full h-[500px] flex flex-col">
        <div 
          ref={scrollContainerRef}
          className={`flex-1 ${showScrollbar ? 'overflow-x-auto' : ''}`}
          style={{ 
            scrollbarWidth: 'thin',
            scrollbarColor: '#cbd5e0 #f7fafc',
            outline: 'none'
          }}
          tabIndex={-1}
        >
        <div style={{ 
          minWidth: showScrollbar ? `${Math.max(800, data.length * (dynamicBarSize + 50))}px` : '100%', 
          width: showScrollbar ? `${Math.max(800, data.length * (dynamicBarSize + 50))}px` : '100%',
          height: '100%'
        }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={visibleData}
              margin={{
                top: 20,
                right: 6,
                left: 6,
                bottom: -20,
              }}
              barCategoryGap="25%"
            >
              <XAxis 
                dataKey="name"
                tick={renderXAxisTick}
                height={90}
                interval={0}
                tickMargin={8}
               
              />
              <YAxis 
                domain={computedDomain}
                label={{ 
                  value: yAxisLabel, 
                  angle: -90, 
                  position: 'insideLeft',
                  offset: 4,
                  style: { textAnchor: 'middle', fontSize: 12, fill: 'var(--text-primary)' }
                }}
                tick={{ fontSize: 10, fill: 'var(--text-primary)' }}
              />
              
              {showReferenceLine && (
                <ReferenceLine y={referenceLineValue} stroke="#666" strokeDasharray="3 3" />
              )}
              
              <Bar 
                dataKey="value" 
                fill="#003C73"
                maxBarSize={dynamicBarSize}
              >
                <LabelList 
                  dataKey="value" 
                  position="top" 
                  formatter={valueFormatter}
                  fontSize={10}
                  fill="var(--text-primary)"
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Fixed X-axis label */}
      <div className="h-10 flex items-center justify-center bg-grayBgNormal">
        <div className="text-xs text-textPrimary font-medium">
          {xAxisLabel}
        </div>
      </div>
    </div>
    </>
  );
};

export default VerticalBarChart;
